// BillingPlans.jsx
import React, { useCallback, useState } from "react";
import { Box, Chip, Grid, Typography, Button, Link, Tooltip } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { PLANS_META, PLAN_QUOTAS } from "@routebot/constants";
import { useTranslation } from "react-i18next";
import { getCurrentLanguage } from "shared/utility/lang";
import AppButton from "shared/components/FormElements/Button";
import PlanCard from "./PlanCard";

function formatPeriodDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const locale = getCurrentLanguage() === "tr" ? "tr-TR" : "en-US";
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

const PlanCards = ({
  currentPlanKey,
  onChangePlan,
  onReactivatePlan,
  onOpenPortal,
  cancelAtPeriodEnd,
  periodEndDate,
  isCanceling = false,
  isReactivating = false,
  plansMeta = PLANS_META,
  hadSubscribedBefore = false,
}) => {
  const { t } = useTranslation();
  const currentPlan = plansMeta?.[currentPlanKey];
  const [submittingPlanKey, setSubmittingPlanKey] = useState(null);
  const [emailCopied, setEmailCopied] = useState(false);
  const [isOpeningBilling, setIsOpeningBilling] = useState(false);

  // Check if current plan requires contact for changes (Enterprise, Unlimited, etc.)
  const isContactRequiredPlan = currentPlan?.contactRequired === true;

  const handleOpenBilling = useCallback(async () => {
    if (!onOpenPortal || isOpeningBilling) return;
    setIsOpeningBilling(true);
    try {
      await onOpenPortal();
    } catch {
      setIsOpeningBilling(false);
    }
  }, [onOpenPortal, isOpeningBilling]);

  const handleCopyEmail = useCallback(async (e) => {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText("vip@routebot.com");
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (err) {
      // Fallback: open mailto
      window.location.href = "mailto:vip@routebot.com";
    }
  }, []);

  const handleChangePlan = useCallback(
    async (planKey) => {
      if (!onChangePlan || submittingPlanKey) return;
      setSubmittingPlanKey(planKey);
      try {
        await onChangePlan(planKey);
      } finally {
        setSubmittingPlanKey(null);
      }
    },
    [onChangePlan, submittingPlanKey],
  );

  // Show other plans in the grid (exclude hidden ones)
  const visiblePlans = Object.values(plansMeta || {}).filter((plan) => !plan?.dontShow);

  // Get monthly map quota for current plan
  const currentPlanQuotas = PLAN_QUOTAS[currentPlanKey] || {};
  const currentMonthlyQuota = Object.values(currentPlanQuotas)[0] || 0;
  const formattedCurrentQuota =
    currentMonthlyQuota >= 1000 ? `${(currentMonthlyQuota / 1000).toLocaleString()}K` : currentMonthlyQuota;

  // For contact-required plans (Enterprise, Unlimited), show plan name only without price
  const routeCount = currentPlan?.maxRoutes === Infinity ? "∞" : (currentPlan?.maxRoutes ?? "∞");
  const summaryDetails = currentPlan
    ? t("subscription.summaryDetails", {
        routes: routeCount,
        quota: formattedCurrentQuota,
      })
    : null;
  const priceLabel =
    currentPlan && !isContactRequiredPlan && Number(currentPlan.priceMonthly) > 0
      ? t("subscription.pricePerMonth", {
          price: Number(currentPlan.priceMonthly).toLocaleString(getCurrentLanguage() === "tr" ? "tr-TR" : "en-US"),
        })
      : null;

  // Cancel plan button is commented out below. Manage billing is the path to cancel.
  // const showCancel = currentPlan && Number(currentPlan.priceMonthly) > 0 && !isContactRequiredPlan;

  return (
    <Box>
      <h3 style={{ marginBottom: "10px", marginTop: "0px" }}>{t("subscription.activePlanLabel")}</h3>
      {currentPlan && (
        <Box
          sx={{
            mb: 3,
            display: "flex",
            flexDirection: "column",
            background: "var(--color-gray-hover)",
            borderRadius: "8px",
            padding: "12px 16px",
            maxWidth: "720px",
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minWidth: 0 }}>
                <Chip label={currentPlan.name} color="primary" size="small" sx={{ fontWeight: 600 }} />
                {priceLabel && (
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    {priceLabel}
                    {Number(currentPlan.pricePerRoute) > 0 && (
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                        {t("subscription.pricePerRoute", { price: currentPlan.pricePerRoute })}
                      </Typography>
                    )}
                  </Typography>
                )}
              </Box>
              {onOpenPortal && hadSubscribedBefore && !isContactRequiredPlan && (
                <AppButton
                  type="button"
                  size="small"
                  containerStyle={{ marginTop: 0, flexShrink: 0 }}
                  onClick={handleOpenBilling}
                  disabled={isOpeningBilling || isCanceling || isReactivating || !!submittingPlanKey}
                >
                  {t("subscription.manageBilling")}
                </AppButton>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {summaryDetails}
            </Typography>
            {periodEndDate && !cancelAtPeriodEnd && !isContactRequiredPlan && formatPeriodDate(periodEndDate) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t("subscription.renewsOn", { date: formatPeriodDate(periodEndDate) })}
              </Typography>
            )}
            {/* Show contact message for Enterprise/Unlimited plans */}
            {isContactRequiredPlan && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t("subscription.contactForChanges").split("vip@routebot.com")[0]}
                <Tooltip title={emailCopied ? t("copied") || "Copied!" : t("clickToCopy") || "Click to copy"} arrow>
                  <Link
                    href="mailto:vip@routebot.com"
                    onClick={handleCopyEmail}
                    sx={{
                      color: "primary.main",
                      textDecoration: "none",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    vip@routebot.com
                    {emailCopied ? (
                      <CheckIcon sx={{ fontSize: 14, color: "success.main" }} />
                    ) : (
                      <ContentCopyIcon sx={{ fontSize: 14, opacity: 0.7 }} />
                    )}
                  </Link>
                </Tooltip>
                {t("subscription.contactForChanges").split("vip@routebot.com")[1] || ""}
              </Typography>
            )}
            {cancelAtPeriodEnd && periodEndDate && !isContactRequiredPlan && (
              <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                {t("subscription.cancelScheduled", {
                  date: formatPeriodDate(periodEndDate) || periodEndDate,
                })}
              </Typography>
            )}
          </Box>

          {cancelAtPeriodEnd && onReactivatePlan && !isContactRequiredPlan && (
            <Box sx={{ mt: 1.5 }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                sx={{ textTransform: "none" }}
                onClick={onReactivatePlan}
                disabled={isCanceling || isReactivating || !!submittingPlanKey || isOpeningBilling}
                startIcon={isReactivating ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {t("subscription.reactivatePlan")}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Don't show available plans section for contact-required plans */}
      {!isContactRequiredPlan && (
        <>
          <h3 style={{ marginBottom: "10px", marginTop: "0px" }}>{t("subscription.availablePlans")}</h3>

          <Grid container spacing={3}>
            {visiblePlans.map((plan) => {
              const isCurrent = plan.key === currentPlanKey;
              const currentTier = currentPlan?.tier ?? 0;
              const planTier = plan?.tier ?? 0;

              // Use tier-based comparison for upgrade/downgrade
              const isUpgrade = !isCurrent && planTier > currentTier;
              const isDowngrade = !isCurrent && planTier < currentTier;

              return (
                <Grid item xs={12} md={4} key={plan.key}>
                  <PlanCard
                    plan={plan}
                    isCurrent={isCurrent}
                    isUpgrade={isUpgrade}
                    isDowngrade={isDowngrade}
                    onChangePlan={(key, meta) => handleChangePlan(key, meta)}
                    isSubmittingGlobal={!!submittingPlanKey || isCanceling || isReactivating}
                    isSubmitting={submittingPlanKey === plan.key}
                  />
                </Grid>
              );
            })}
          </Grid>

          {/* All plans include banner */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <CheckIcon sx={{ color: "success.main", fontSize: 24 }} />
            <Typography variant="body1" sx={{ color: "#2e7d32", fontWeight: 500 }}>
              {t("subscription.allPlansIncludeDescription")}
            </Typography>
          </Box>

          {/* Explanation of terms */}
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="body2" sx={{ color: "#6b7280", fontSize: "13px" }}>
              {t("subscription.activeRoutesExplanation")}
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b7280", fontSize: "13px" }}>
              {t("subscription.mapOperationsExplanation")}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PlanCards;
